import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

/* --------------------------------------------------------------------------
   Keyboard: Enter = Save, Esc = Cancel
   -------------------------------------------------------------------------- */

export interface DialogKeysOptions {
  onSave?: () => void;
  onCancel?: () => void;
  saveDisabled?: boolean;
  saving?: boolean;
  /** Default true. */
  enabled?: boolean;
  /**
   * 'self' (default): the keys count when focus is inside the element `ref` points at
   *   (put the ref on the Dialog's Paper or the form).
   * 'nearest': `ref` is an element inside the dialog; the keys count anywhere in the
   *   nearest dialog (role="dialog"/"alertdialog") or, failing that, <form> around it.
   */
  scope?: 'self' | 'nearest';
  /**
   * Esc: 'auto' (default) calls onCancel unless the scope is inside an MUI Dialog, whose
   * own onClose already answers Esc. 'always' / 'never' force it.
   */
  escape?: 'auto' | 'always' | 'never';
}

/** Where Enter belongs to the control itself, never to Save. */
const ENTER_OWNERS = [
  'textarea',
  'button',
  'a[href]',
  'select',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  'input[type="file"]',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="tree"]',
  '[role="grid"]',
  '[role="treegrid"]',
  '[aria-expanded="true"]',
  '[data-enter-ignore]',
].join(',');

/** An open dropdown keeps Esc for itself (it closes the list, not the dialog). */
const ESC_OWNERS = '[aria-expanded="true"],[data-esc-ignore]';

/** Text-entry inputs: the fields whose typed value can wait for focus to leave. */
const NOT_TEXT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'image', 'range', 'color', 'hidden']);

/**
 * Blur, then refocus, the text field Enter was pressed in, so a field that commits
 * on blur has committed before Save runs. Anything else is left alone.
 */
function commitFocusedField(target: HTMLElement): void {
  if (!(target instanceof HTMLInputElement) || NOT_TEXT.has(target.type)) return;
  // A combobox commits by picking an option, and refocusing one can reopen its list.
  if (document.activeElement !== target || target.getAttribute('role') === 'combobox') return;
  target.blur();
  if (target.isConnected && !target.disabled) target.focus({ preventScroll: true });
}

function scopeOf(el: HTMLElement, scope: 'self' | 'nearest'): HTMLElement {
  if (scope === 'self') return el;
  return (
    el.closest<HTMLElement>('[role="dialog"],[role="alertdialog"]') ??
    el.closest<HTMLElement>('form') ??
    el.parentElement ??
    el
  );
}

/**
 * Enter = Save and Esc = Cancel for a dialog or form. DialogActionsBar calls this for you;
 * use it directly only where the buttons cannot be a DialogActionsBar.
 *
 * Enter first commits the focused text field (blur, then focus again) and saves on the
 * next tick, so the value saved is the one just typed. A DateField / TimeField keeps
 * Enter for itself (Enter there only accepts the date or time).
 *
 * Listens on the document (after React's own handlers), so a field that keeps Enter for
 * itself by calling preventDefault() or stopPropagation() (an open Autocomplete picking an
 * option, a grid cell editor) wins. Enter never saves from a textarea (Ctrl+Enter does),
 * a button or link (Enter clicks it), an open dropdown, a grid or list, or anything marked
 * `data-enter-ignore`. Keys inside a nested dialog (a confirm on top) never reach the outer one.
 */
export function useDialogKeys(ref: RefObject<HTMLElement | null>, options: DialogKeysOptions): void {
  const opts = useRef(options);
  useLayoutEffect(() => {
    opts.current = options;
  });
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const anchor = ref.current;
      const target = e.target as HTMLElement | null;
      if (!anchor || !target || !(target instanceof HTMLElement)) return;
      const o = opts.current;
      const container = scopeOf(anchor, o.scope ?? 'self');
      if (!container.contains(target)) return;

      if (e.key === 'Enter') {
        if (e.repeat || e.altKey || e.shiftKey) return;
        const ctrl = e.ctrlKey || e.metaKey;
        // Ctrl+Enter saves from anywhere except a button / link (where Enter is its click).
        if (ctrl) {
          if (target.closest('button,a[href],[role="button"]')) return;
        } else if (target.closest(ENTER_OWNERS)) {
          return;
        }
        if (!o.onSave) return;
        e.preventDefault(); // no native form submit on top of ours
        if (o.saving) return;
        // Commit the field first: one that takes its typed value when focus leaves
        // (a NumberField, a commit-on-blur text box) gets it
        // now, and the focus goes straight back so the keys keep working.
        commitFocusedField(target);
        // Save on the next tick, when the committed value has re-rendered the dialog,
        // so Save (and whether it is enabled at all) sees the new value, never the old.
        window.setTimeout(() => {
          const now = opts.current;
          if (!now.saveDisabled && !now.saving) now.onSave?.();
        }, 0);
        return;
      }

      if (e.key === 'Escape') {
        if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
        const mode = o.escape ?? 'auto';
        if (mode === 'never') return;
        if (mode === 'auto' && container.closest('.MuiDialog-root')) return;
        if (target.closest(ESC_OWNERS)) return;
        if (!o.onCancel || o.saving) return;
        e.preventDefault();
        o.onCancel();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enabled, ref]);
}
